import {
  AnimationAction,
  type AnimationClip,
  AnimationMixer,
  type Material,
  MeshStandardMaterial,
  type Object3D,
  type Scene,
  SkinnedMesh,
  type Color
} from 'three'
import { clone as clone_skeleton_hierarchy } from 'three/examples/jsm/utils/SkeletonUtils.js'

/**
 * OnionSkinManager
 *
 * Renders ghost copies of the active SkinnedMesh at offset times around the
 * playhead so the animator can see "where the model is coming from" and
 *  "where it's going to" — the classic 2D animation onion-skin overlay,
 * adapted for skeletal 3D.
 *
 * Each ghost is a deep clone of the source mesh + skeleton (via Three's
 * SkeletonUtils.clone), tinted and translucent. A dedicated AnimationMixer
 * per ghost lets us scrub each one to a different time without touching the
 * source's mixer.
 *
 * Past frames render warm-tinted (red), future frames cool-tinted (blue),
 * with opacity falling off with distance from the playhead.
 */

export interface OnionSkinOptions {
  /** Time offsets (in seconds) at which to render ghost frames, e.g. [-0.2, -0.1, 0.1, 0.2]. */
  offsets?: number[]
  /** Base opacity at offset = 0; falls off linearly to ~0.05 at the furthest offset. */
  base_opacity?: number
  /** Color for past-frame ghosts (negative offset). */
  past_color?: number
  /** Color for future-frame ghosts (positive offset). */
  future_color?: number
}

interface Ghost {
  mesh: SkinnedMesh
  root_object: Object3D
  mixer: AnimationMixer
  action: AnimationAction | null
  offset: number
  cloned_materials: Material[]
}

export class OnionSkinManager {
  private readonly scene: Scene
  private ghosts: Ghost[] = []
  private source: SkinnedMesh | null = null
  private current_clip: AnimationClip | null = null
  private enabled: boolean = false

  private readonly offsets: number[]
  private readonly base_opacity: number
  private readonly past_color: number
  private readonly future_color: number

  constructor (scene: Scene, options: OnionSkinOptions = {}) {
    this.scene = scene
    this.offsets = options.offsets ?? [-0.2, -0.1, 0.1, 0.2]
    this.base_opacity = options.base_opacity ?? 0.35
    this.past_color = options.past_color ?? 0xff5555
    this.future_color = options.future_color ?? 0x5599ff
  }

  is_enabled (): boolean { return this.enabled }

  /**
   * Spawn ghost meshes from `source` posed by `clip`. Replaces any prior
   * ghost set. Idempotent — safe to call when already enabled (re-creates).
   */
  enable (source: SkinnedMesh, clip: AnimationClip): void {
    this.disable()
    this.source = source
    this.current_clip = clip

    const max_abs_offset = this.offsets.reduce((m, o) => Math.max(m, Math.abs(o)), 0)
    for (const offset of this.offsets) {
      const ghost = this.build_ghost(source, clip, offset, max_abs_offset)
      if (ghost === null) continue
      this.ghosts.push(ghost)
      this.scene.add(ghost.root_object)
    }
    this.enabled = true
  }

  /**
   * Update each ghost's pose. Call once per frame from the render loop with
   * the live playhead time. No-op when disabled.
   */
  update (current_time: number): void {
    if (!this.enabled || this.current_clip === null) return
    const duration = Math.max(this.current_clip.duration, 1e-3)
    for (const ghost of this.ghosts) {
      if (ghost.action === null) continue
      const target_time = current_time + ghost.offset
      // Wrap into [0, duration) so ghosts at clip edges show the looped pose.
      const wrapped = ((target_time % duration) + duration) % duration
      ghost.action.time = wrapped
      ghost.mixer.update(0)
    }
  }

  /**
   * Swap the clip backing each ghost (called when the user switches the
   * active animation clip). Cheap — re-uses the same ghost meshes.
   */
  set_clip (clip: AnimationClip): void {
    if (!this.enabled) return
    this.current_clip = clip
    for (const ghost of this.ghosts) {
      ghost.mixer.stopAllAction()
      ghost.action = ghost.mixer.clipAction(clip)
      ghost.action.play()
    }
  }

  /** Tear down ghost meshes + materials + mixers. Idempotent. */
  disable (): void {
    for (const ghost of this.ghosts) {
      this.scene.remove(ghost.root_object)
      ghost.mixer.stopAllAction()
      try { ghost.mixer.uncacheRoot(ghost.mesh) } catch (_) { /* best effort */ }
      for (const mat of ghost.cloned_materials) {
        try { mat.dispose() } catch (_) { /* best effort */ }
      }
    }
    this.ghosts = []
    this.source = null
    this.current_clip = null
    this.enabled = false
  }

  private build_ghost (
    source: SkinnedMesh,
    clip: AnimationClip,
    offset: number,
    max_abs_offset: number
  ): Ghost | null {
    // SkeletonUtils.clone clones the entire hierarchy including skinned
    // meshes and their associated skeletons, so the ghost is independently
    // poseable. Fall back to source itself if the source has no parent (rare
    // but defensive).
    const root_to_clone: Object3D = source.parent ?? source
    const cloned_root = clone_skeleton_hierarchy(root_to_clone) as Object3D
    cloned_root.name = `OnionGhost(${offset.toFixed(2)}s)`

    let cloned_skinned_mesh: SkinnedMesh | null = null
    cloned_root.traverse((child) => {
      if (cloned_skinned_mesh === null && child instanceof SkinnedMesh) {
        cloned_skinned_mesh = child
      }
    })
    if (cloned_skinned_mesh === null) return null

    // Tint + translucency. Opacity falls off with distance from playhead.
    const distance_factor = max_abs_offset > 0 ? 1 - Math.abs(offset) / max_abs_offset : 1
    const opacity = Math.max(0.05, this.base_opacity * distance_factor)
    const tint = offset < 0 ? this.past_color : this.future_color
    const cloned_materials = this.recolor_materials(cloned_skinned_mesh, opacity, tint)

    const mixer = new AnimationMixer(cloned_skinned_mesh)
    const action = mixer.clipAction(clip)
    action.play()

    return {
      mesh: cloned_skinned_mesh,
      root_object: cloned_root,
      mixer,
      action,
      offset,
      cloned_materials
    }
  }

  /**
   * Replace the mesh's materials with cloned copies that are translucent +
   * tinted toward `tint_hex`. Returns the cloned materials so the caller can
   * dispose them on teardown. We blend the original albedo with the tint
   * rather than overwriting it, so the silhouette stays recognizable.
   */
  private recolor_materials (mesh: SkinnedMesh, opacity: number, tint_hex: number): Material[] {
    const original = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    const cloned: Material[] = []
    const new_mats: Material[] = []
    for (const mat of original) {
      const clone = (mat as Material).clone() as MeshStandardMaterial
      const color: Color | undefined = (clone as { color?: Color }).color
      if (color !== undefined) {
        const tint_r = ((tint_hex >> 16) & 0xff) / 255
        const tint_g = ((tint_hex >> 8) & 0xff) / 255
        const tint_b = (tint_hex & 0xff) / 255
        // 60/40 mix: keep some original color so different ghosts don't look
        // identical when the source has multi-colored materials.
        color.setRGB(
          color.r * 0.4 + tint_r * 0.6,
          color.g * 0.4 + tint_g * 0.6,
          color.b * 0.4 + tint_b * 0.6
        )
      }
      clone.transparent = true
      clone.opacity = opacity
      clone.depthWrite = false
      // Disable shadow casting from ghosts — shadow accumulation looks bad.
      clone.side = (mat as MeshStandardMaterial).side
      cloned.push(clone)
      new_mats.push(clone)
    }
    mesh.material = Array.isArray(mesh.material) ? new_mats : new_mats[0]
    mesh.castShadow = false
    mesh.receiveShadow = false
    return cloned
  }
}
